/**
 * The Partners pages' reads (owner D19, 2026-10-14). The URLs are declared here where
 * `api-requests.test.ts` can execute them against the Worker catalog, never inside a `useQuery` closure.
 * Lists are LIVE_LIST (staff work through them), a partner or an application is a DETAIL read and the
 * overview, fees and settings are AGGREGATE reads.
 */
import { apiGet } from "@/lib/api";
import { AGGREGATE, DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type {
  ApplicationDetail,
  ApplicationList,
  EarningsList,
  PartnerComplianceAgentView,
  PartnerDetail,
  PartnerFeeDefaults,
  PartnerList,
  PartnerSettings,
  Paged,
  PartnersOverview,
  PayoutBatchDetail,
  PayoutBatchList,
} from "@/types-partners";

const PAGE_SIZE = 25;

/**
 * A renamed or missing field should blank one cell, never break a screen (error-paths.test.ts renders every
 * page against `{}`): each read normalises its answer in `select`, so the pages read arrays and counters
 * without a guard on every line. Nothing is invented for a record that is absent: a detail with no
 * `partner` or `batch` stays without it and its page says the record was not found.
 */
function paged<T>(data: Partial<Paged<T>> | undefined): Paged<T> {
  return {
    total: data?.total ?? 0,
    page: data?.page ?? 1,
    pageSize: data?.pageSize ?? PAGE_SIZE,
    items: data?.items ?? [],
  };
}

function normalizeOverview(data: Partial<PartnersOverview>): PartnersOverview {
  return {
    pendingApplications: 0,
    oldestPendingAt: null,
    heldEarnings: 0,
    heldCents: 0,
    openRiskEvents: 0,
    draftBatch: null,
    minPayoutCents: 0,
    programEnabled: false,
    feesConfigured: false,
    ...data,
    partners: { approved: 0, deactivated: 0, banned: 0, ...data.partners },
    totals: {
      pendingCents: 0,
      payableCents: 0,
      paidCents: 0,
      payablePartners: 0,
      clicks30d: 0,
      activeAttributions: 0,
      ...data.totals,
    },
  };
}

function normalizeApplications(
  data: Partial<ApplicationList>,
): ApplicationList {
  const list = paged(data);
  return {
    ...list,
    oldestPending: data.oldestPending ?? null,
    items: list.items.map((item) => ({
      ...item,
      riskBadges: item.riskBadges ?? [],
    })),
  };
}

function normalizeApplication(
  data: Partial<ApplicationDetail>,
): ApplicationDetail {
  return {
    ...data,
    answers: data.answers ?? {},
    riskBadges: data.riskBadges ?? [],
    otherApplications: data.otherApplications ?? [],
    feeDefaults: data.feeDefaults ?? [],
    audit: data.audit ?? [],
  } as ApplicationDetail;
}

function normalizeDetail(data: Partial<PartnerDetail>): PartnerDetail {
  return {
    ...data,
    members: data.members ?? [],
    codes: data.codes ?? [],
    feeTable: data.feeTable ?? [],
    campaigns: data.campaigns ?? [],
    referrals: data.referrals ?? [],
    earnings: data.earnings ?? [],
    payouts: data.payouts ?? [],
    payoutMethod: data.payoutMethod ?? null,
    taxForms: data.taxForms ?? [],
    screenings: data.screenings ?? [],
    riskEvents: data.riskEvents ?? [],
    audit: data.audit ?? [],
    balance: {
      pendingCents: 0,
      heldCents: 0,
      payableCents: 0,
      paidCents: 0,
      ...data.balance,
    },
    duplicateDestination: {
      partnerIds: [],
      blocking: false,
      ...data.duplicateDestination,
    },
    signIns: { count: 0, lastAt: null, retentionDays: 60, ...data.signIns },
    totals: {
      referrals: 0,
      earnings: 0,
      heldEarnings: 0,
      payouts: 0,
      riskEvents: 0,
      audit: 0,
      ...data.totals,
    },
    limits: {
      referrals: 200,
      earnings: 200,
      payouts: 50,
      riskEvents: 100,
      audit: 100,
      ...data.limits,
    },
    banPreview: {
      rejectedFees: 0,
      rejectedCents: 0,
      clawbacksKept: 0,
      draftLines: 0,
      exportedLines: 0,
      ...data.banPreview,
    },
  } as PartnerDetail;
}

function normalizeEarnings(data: Partial<EarningsList>): EarningsList {
  const list = paged(data);
  return {
    ...list,
    counts: data.counts ?? [],
    items: list.items.map((item) => ({
      ...item,
      riskEvents: item.riskEvents ?? [],
    })),
  };
}

function normalizeSettings(data: Partial<PartnerSettings>): PartnerSettings {
  return {
    enabled: false,
    termsVersion: "",
    referralCookieRequiresConsent: false,
    minPayoutCents: 0,
    holdDays: 0,
    windowDays: 0,
    updatedBy: null,
    updatedAt: null,
    ...data,
    bannedReferrers: data.bannedReferrers ?? [],
  };
}

export function partnersOverviewQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["partners", "overview"] as const,
    select: normalizeOverview,
    queryFn: () => apiGet<PartnersOverview>("/api/admin/partners/overview"),
  };
}

export interface PartnerApplicationsParams {
  status: string;
  search?: string;
  page: number;
}
export function partnerApplicationsQuery(params: PartnerApplicationsParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "partners",
      "applications",
      params.status,
      params.search ?? "",
      params.page,
    ] as const,
    select: normalizeApplications,
    queryFn: () =>
      apiGet<ApplicationList>("/api/admin/partners/applications", {
        status: params.status || undefined,
        search: params.search || undefined,
        page: params.page,
        pageSize: PAGE_SIZE,
      }),
  };
}

export function partnerApplicationQuery(applicationId: string) {
  return {
    ...DETAIL,
    queryKey: ["partners", "application", applicationId] as const,
    select: normalizeApplication,
    queryFn: () =>
      apiGet<ApplicationDetail>(
        `/api/admin/partners/applications/${encodeURIComponent(applicationId)}`,
      ),
  };
}

export interface PartnerListParams {
  status: string;
  search?: string;
  page: number;
}
export function partnerListQuery(params: PartnerListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "partners",
      "list",
      params.status,
      params.search ?? "",
      params.page,
    ] as const,
    select: (data: Partial<PartnerList>) => paged(data),
    queryFn: () =>
      apiGet<PartnerList>("/api/admin/partners", {
        status: params.status || undefined,
        search: params.search || undefined,
        page: params.page,
        pageSize: PAGE_SIZE,
      }),
  };
}

export function partnerDetailQuery(partnerId: string) {
  return {
    ...DETAIL,
    queryKey: ["partners", "detail", partnerId] as const,
    select: normalizeDetail,
    queryFn: () =>
      apiGet<PartnerDetail>(
        `/api/admin/partners/${encodeURIComponent(partnerId)}`,
      ),
  };
}

export interface PartnerEarningsParams {
  status: string;
  partnerId?: string;
  page: number;
}
export function partnerEarningsQuery(params: PartnerEarningsParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "partners",
      "earnings",
      params.status,
      params.partnerId ?? "",
      params.page,
    ] as const,
    select: normalizeEarnings,
    queryFn: () =>
      apiGet<EarningsList>("/api/admin/partners/earnings", {
        status: params.status || undefined,
        partnerId: params.partnerId || undefined,
        page: params.page,
        pageSize: PAGE_SIZE,
      }),
  };
}

export function partnerFeesQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["partners", "fees"] as const,
    select: (data: Partial<PartnerFeeDefaults>): PartnerFeeDefaults => ({
      defaults: data.defaults ?? [],
    }),
    queryFn: () => apiGet<PartnerFeeDefaults>("/api/admin/partners/fees"),
  };
}

export function partnerSettingsQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["partners", "settings"] as const,
    select: normalizeSettings,
    queryFn: () => apiGet<PartnerSettings>("/api/admin/partners/settings"),
  };
}

export function partnerPayoutBatchesQuery() {
  return {
    ...LIVE_LIST,
    queryKey: ["partners", "payouts"] as const,
    select: (data: Partial<PayoutBatchList>): PayoutBatchList => ({
      batches: data.batches ?? [],
    }),
    queryFn: () => apiGet<PayoutBatchList>("/api/admin/partners/payouts"),
  };
}

export function partnerPayoutBatchQuery(batchId: string) {
  return {
    ...DETAIL,
    queryKey: ["partners", "payout-batch", batchId] as const,
    select: (data: Partial<PayoutBatchDetail>) =>
      ({
        ...data,
        lines: data.lines ?? [],
        ineligible: data.ineligible ?? [],
      }) as PayoutBatchDetail,
    queryFn: () =>
      apiGet<PayoutBatchDetail>(
        `/api/admin/partners/payouts/${encodeURIComponent(batchId)}`,
      ),
  };
}

function normalizeComplianceAgent(
  data: Partial<PartnerComplianceAgentView>,
): PartnerComplianceAgentView {
  return {
    partnerId: "",
    mode: "off",
    nextRunAt: null,
    lastRun: null,
    ...data,
    runs: data.runs ?? [],
    cases: data.cases ?? [],
    proposals: data.proposals ?? [],
    events: data.events ?? [],
    unfreezeReady: data.unfreezeReady ?? null,
  };
}

/** The AI compliance agent's state for one partner. Under ["partners"], so every partner write refreshes it. */
export function partnerComplianceAgentQuery(partnerId: string) {
  return {
    ...DETAIL,
    queryKey: ["partners", "compliance-agent", partnerId] as const,
    select: normalizeComplianceAgent,
    queryFn: () =>
      apiGet<PartnerComplianceAgentView>(
        `/api/admin/partners/${encodeURIComponent(partnerId)}/compliance-agent`,
      ),
  };
}
